import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import ImmersiveDetailView from './ImmersiveDetailView';

const Container = styled.div`
  position: relative;
  padding: ${({ theme }) => theme.spacing.immersiveWorker.desktop.containerPadding};
  background: #f8f9fa;

  @media (max-width: 768px) {
    margin: 0 -${({ theme }) => theme.spacing.immersiveWorker.mobile.negativeMargin};
    padding: ${({ theme }) => theme.spacing.immersiveWorker.mobile.containerPadding};
  }
`;

const Section = styled.section`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  margin-bottom: 16px;
  padding: 0 ${({ theme }) => theme.spacing.immersiveWorker.desktop.contentPadding};

  @media (max-width: 768px) {
    padding: 0 ${({ theme }) => theme.spacing.immersiveWorker.mobile.contentPadding};
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
`;

const WorkerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.immersiveWorker.desktop.gap};
  padding: 0 ${({ theme }) => theme.spacing.immersiveWorker.desktop.contentPadding};

  @media (max-width: 768px) {
    gap: calc(${({ theme }) => theme.spacing.immersiveWorker.mobile.gap} - 5px);
    padding: 0 calc(${({ theme }) => theme.spacing.immersiveWorker.mobile.contentPadding} - 5px);
  }
`;

const WorkerCard = styled(motion.div)`
  position: relative;
  aspect-ratio: 1;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

const WorkerImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: ${props => `url(${props.image})`};
  background-size: cover;
  background-position: center;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) 70%,
      rgba(0, 0, 0, 0.8) 100%
    );
  }
`;

const WorkerInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px;
  color: white;
  z-index: 1;
  text-align: left;
`;

const WorkerRole = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    font-size: 12px;
    font-weight: 700;
  }
`;

const ImmersiveWorkerView = () => {
  const { t, tSafe } = useLanguage();
  const [activeWorker, setActiveWorker] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const departments = Object.keys(tSafe('digitalWorkers.departments', 'object'));

  const handleWorkerClick = (worker) => {
    setActiveWorker(worker);
    setShowDetail(true);
  };

  const handleClose = () => {
    setShowDetail(false);
    setActiveWorker(null);
  };

  return (
    <Container>
      {departments.map(department => {
        const workers = tSafe(`digitalWorkers.departments.${department}.workers`, 'array');
        
        return (
          <Section key={department}>
            <SectionHeader>
              <SectionTitle>
                {t(`digitalWorkers.departments.${department}.name`)}
              </SectionTitle>
            </SectionHeader>

            <WorkerGrid>
              {workers.map(worker => (
                <WorkerCard
                  key={worker}
                  onClick={() => handleWorkerClick(worker)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <WorkerImage
                    image={t(`digitalWorkers.workers.${worker}.profile.media.image`)}
                  />
                  <WorkerInfo>
                    <WorkerRole>
                      {t(`digitalWorkers.workers.${worker}.profile.role`)}
                    </WorkerRole>
                  </WorkerInfo>
                </WorkerCard>
              ))}
            </WorkerGrid>
          </Section>
        );
      })}

      <AnimatePresence>
        {showDetail && activeWorker && (
          <ImmersiveDetailView
            worker={activeWorker}
            onClose={handleClose}
          />
        )}
      </AnimatePresence>
    </Container>
  );
};

export default ImmersiveWorkerView; 